import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bell, BellOff, CheckCheck, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/library/popover";
import { ScrollArea } from "@/components/ui/library/scroll-area";
import { useAppSelector } from "@/stores/hook";
import {
  useNotificationData,
  useMarkAllRead,
  useDismissNotification,
  useClearAllNotifications,
  useMarkRead,
  setNotificationDropdownOpen,
  type NotificationFilters as NotificationFilterParams,
} from "../api/useNotifications";
import { FILTER_TO_TYPE_MAP } from "../types";
import NotificationFilters from "./NotificationFilters";
import NotificationItem from "./NotificationItem";

export default function NotificationDropdown() {
  const {
    activeFilter,
    selectedStages,
    selectedStatuses,
    selectedSubStatuses,
  } = useAppSelector((state) => state.notifications);

  const [open, setOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const navigatingRef = useRef(false);

  // Close without Popover restoring focus (which kills the Sheet)
  const closeForNavigation = () => {
    navigatingRef.current = true;
    setOpen(false);
  };

  // Push filters to the server instead of slicing the already-loaded
  // pages on the client. Filtering 20 cached notifications down to ones
  // matching "Stage = Enquiry" hit zero results the moment none of the
  // first page was a stage_change. Now the server returns the right
  // type/value bucket directly and infinite scroll pages through it.
  const filterParams = useMemo<NotificationFilterParams>(() => {
    if (activeFilter === "all") return {};
    const type = FILTER_TO_TYPE_MAP[activeFilter];
    if (!type) return {};
    let values: string[] | undefined;
    if (activeFilter === "stage" && selectedStages.length > 0) {
      values = selectedStages;
    } else if (activeFilter === "status" && selectedStatuses.length > 0) {
      values = selectedStatuses;
    } else if (
      activeFilter === "sub_status" &&
      selectedSubStatuses.length > 0
    ) {
      values = selectedSubStatuses;
    }
    return { type, values };
  }, [activeFilter, selectedStages, selectedStatuses, selectedSubStatuses]);

  const { notifications, unreadCount, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useNotificationData(filterParams);

  const markAllRead = useMarkAllRead();
  const markRead = useMarkRead();
  const dismissNotification = useDismissNotification();
  const clearAll = useClearAllNotifications();

  const filtered = notifications;

  // Socket pushes patch the list cache in place; the ONLY time a push
  // triggers a refetch is while this popover is open (and even then it is
  // coalesced) — so tell the api layer when that is.
  useEffect(() => {
    setNotificationDropdownOpen(open);
    return () => setNotificationDropdownOpen(false);
  }, [open]);

  // Keep latest values out of the handler closure so we don't re-attach
  // the scroll listener every time react-query flips isFetchingNextPage.
  const fetchNextPageRef = useRef(fetchNextPage);
  const hasNextPageRef = useRef(hasNextPage);
  const isFetchingNextPageRef = useRef(isFetchingNextPage);
  fetchNextPageRef.current = fetchNextPage;
  hasNextPageRef.current = hasNextPage;
  isFetchingNextPageRef.current = isFetchingNextPage;

  const handleScroll = useCallback((viewport: HTMLElement) => {
    if (!hasNextPageRef.current || isFetchingNextPageRef.current) return;
    if (
      viewport.scrollTop + viewport.clientHeight >=
      viewport.scrollHeight - 40
    ) {
      fetchNextPageRef.current();
    }
  }, []);

  // The Popover content is rendered via a Radix Portal that mounts AFTER
  // `open` flips to true, so the ScrollArea viewport may not exist on the
  // first `useEffect` pass. Poll briefly for it instead of giving up.
  useEffect(() => {
    if (!open) return;

    let viewport: HTMLElement | null = null;
    let attachedListener: ((e: Event) => void) | null = null;
    let detached = false;

    const tryAttach = () => {
      if (detached) return;
      const el = scrollRef.current;
      const v = el?.querySelector(
        "[data-slot='scroll-area-viewport']",
      ) as HTMLElement | null;
      if (!v) {
        requestAnimationFrame(tryAttach);
        return;
      }
      viewport = v;
      attachedListener = () => handleScroll(v);
      v.addEventListener("scroll", attachedListener);
    };

    tryAttach();

    return () => {
      detached = true;
      if (viewport && attachedListener) {
        viewport.removeEventListener("scroll", attachedListener);
      }
    };
  }, [open, handleScroll]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-xl w-[1.5rem] h-[1.5rem] relative"
          data-tour="header-notifications"
        >
          <Bell className="size-5 text-[var(--colorblack-10)]" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 size-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center leading-none">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[400px] p-0 rounded-xl shadow-xl border-[var(--color-black-1)] overflow-visible z-[200]"
        onCloseAutoFocus={(e) => {
          if (navigatingRef.current) {
            e.preventDefault();
            navigatingRef.current = false;
          }
        }}
      >
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-[var(--color-black-1)] rounded-t-xl bg-white">
          <div className="flex items-center gap-2">
            <h3 className="text-[13px] font-semibold text-[var(--color-black-9)] tracking-tight">
              Notifications
            </h3>
            {unreadCount > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-[var(--color-primary)] text-white text-[9px] font-bold leading-none">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <button
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] text-[var(--color-primary)] font-medium hover:bg-[var(--color-green-1)] transition-colors"
                onClick={() => markAllRead.mutate()}
              >
                <CheckCheck className="size-3" />
                Mark all read
              </button>
            )}
          </div>
        </div>

        <NotificationFilters />

        <ScrollArea
          ref={scrollRef}
          className="h-[380px] overflow-hidden rounded-b-none bg-white"
        >
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-full py-12 gap-2">
              <Loader2 className="size-6 text-[var(--color-black-3)] animate-spin" />
              <p className="text-[12px] text-[var(--color-black-5)]">Loading...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-12 gap-2">
              <BellOff className="size-8 text-[var(--color-black-3)]" />
              <p className="text-[12px] text-[var(--color-black-5)]">
                No notifications
              </p>
            </div>
          ) : (
            <>
              {filtered.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onDismiss={(id) => dismissNotification.mutate(id)}
                  onMarkRead={(id) => markRead.mutate(id)}
                  onClose={closeForNavigation}
                />
              ))}
              {isFetchingNextPage && (
                <div className="flex justify-center py-3">
                  <span className="text-[10px] text-[var(--color-black-4)] animate-pulse">
                    Loading more...
                  </span>
                </div>
              )}
            </>
          )}
        </ScrollArea>

        {notifications.length > 0 && (
          <div className="flex items-center justify-center px-3 py-2 border-t border-[var(--color-black-1)] rounded-b-xl bg-white">
            <button
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] text-red-500 font-medium hover:bg-red-50 transition-colors"
              onClick={() => clearAll.mutate()}
            >
              <Trash2 className="size-3" />
              Clear all notifications
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
